import React, { useContext, useState } from 'react';
import { observer } from 'mobx-react';
import { styled } from '@this/constants/themes';
import type { Moment } from 'moment';
import { Box, Typography, Paper, Button, TextField, Dialog } from '@material-ui/core';
import AppointContext from '@this/components/appoint/appoint_context';
import { FixAdjustmentIcon } from '@this/components/appoint/icons';
import muiTheme from '@this/components/appoint/mui_theme';
import { ButtonWrapper, ButtonProgress } from '../../common';

interface Props {
  show: boolean;
  onClose: () => void;
  startTime: Moment;
  endTime: Moment;
  onSubmit: (destinationName: string, destinationMailAddress: string) => void;
  isSubmitting: boolean;
  errorMessage: string | null;
}

const FixAdjustmentDialog = observer(
  ({ show, onClose, startTime, endTime, onSubmit, isSubmitting, errorMessage }: Props) => {
    const contextValue = useContext(AppointContext);
    const [destinationName, setDestinationName] = useState<string>(contextValue?.user?.name || '');
    const [destinationMailAddress, setDestinationMailAddress] = useState<string>(contextValue.user?.email || '');

    const isInputDestination = () => {
      if (destinationName.length === 0 || destinationMailAddress.length === 0) {
        return false;
      }
      const legAddress = /\S+@\S+\.\S+/;
      if (!legAddress.test(destinationMailAddress)) {
        return false;
      }
      return true;
    };

    return (
      <TheDialog
        open={show}
        onClose={onClose}
        maxWidth="md"
        style={contextValue.isMobile ? { zIndex: 99 } : {}}
        PaperProps={{
          style: contextValue.isMobile
            ? {
                width: '100%',
                margin: '15px',
                boxShadow: '0 0'
              }
            : {}
        }}
        isMobile={contextValue.isMobile}
      >
        <FixedPaper isMobile={contextValue.isMobile}>
          <form
            onSubmit={e => {
              e.preventDefault();
              onSubmit(destinationName, destinationMailAddress);
            }}
          >
            <Box padding={contextValue.isMobile ? '30px 25px 20px' : '30px 80px 0'}>
              <FixedHead>
                <FixedHeadIcon>
                  <FixAdjustmentIcon color={muiTheme.palette.primary.main} />
                </FixedHeadIcon>
                <FixedTitle>日程確認</FixedTitle>
                <FixedText>
                  下記の日程でよろしければ、{contextValue.isMobile && <br />}ご連絡先を入力して確定してください
                </FixedText>
              </FixedHead>
              <FixDateTimeBox isMobile={contextValue.isMobile}>
                <FixDateTimeValueBox>
                  {startTime.format('M月D日 (ddd) H:mm 〜 ')}
                  {endTime.format('H:mm')}
                </FixDateTimeValueBox>
              </FixDateTimeBox>
              <FixBox>
                <TheTextField
                  label="お名前"
                  placeholder="例：株式会社トランスファーデータ 山田 太郎"
                  value={destinationName}
                  InputLabelProps={{ shrink: true }}
                  onChange={e => setDestinationName(e.target.value)}
                  style={{ width: '100%' }}
                />
              </FixBox>
              <FixBox>
                <TheTextField
                  label="メールアドレス"
                  placeholder="sample@aitravel.jp"
                  value={destinationMailAddress}
                  InputLabelProps={{ shrink: true }}
                  onChange={e => setDestinationMailAddress(e.target.value)}
                  style={{ width: '100%' }}
                />
              </FixBox>
            </Box>
            <TheButtonWrapper isMobile={contextValue.isMobile}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth={contextValue.isMobile}
                disabled={isSubmitting || !isInputDestination()}
                style={{ margin: '0 auto', display: 'block', paddingLeft: '40px', paddingRight: '40px' }}
              >
                確定
              </Button>
              {isSubmitting && <ButtonProgress />}
              {errorMessage && <Error color="error">{errorMessage}</Error>}
            </TheButtonWrapper>
          </form>
        </FixedPaper>
      </TheDialog>
    );
  }
);

const TheDialog = styled(Dialog)<{ isMobile: boolean }>`
  ${props =>
    props.isMobile &&
    `
    overflow-y: auto;

    .MuiBackdrop-root {
      background-color: #ededed;
    }

    .MuiDialog-container.MuiDialog-scrollPaper {
      max-height: calc(100vh - 54px);
      padding-top: 54px;
      align-items: start;
    }
  `}
`;

const FixedHead = styled(Box)`
  margin-bottom: 25px;
  text-align: center;
`;

const FixedHeadIcon = styled.div`
  margin-bottom: 5px;
  color: ${muiTheme.palette.primary.main};
`;

const FixedTitle = styled(Typography)`
  font-size: 20px;
  font-weight: 700;
`;

const FixedText = styled(Typography)`
  color: ${muiTheme.palette.primary.main};
  font-size: 11px;
  font-weight: 700;
`;

const FixedPaper = styled(Paper)<{ isMobile: boolean }>`
  min-width: 500px;
  box-sizing: border-box;

  ${props =>
    props.isMobile &&
    `
    min-width: auto;
    max-height: calc(100vh - 54px - 15px - 15px);
    box-shadow: 0 0;
  `}
`;

const FixDateTimeBox = styled(Box)<{ isMobile: boolean }>`
  padding: 10px;
  text-align: center;
  background: #f9f9f9;
  border: 1px solid #ededed;
  box-sizing: border-box;
  border-radius: 3px;
`;

const FixDateTimeValueBox = styled(Box)`
  font-weight: bold;
  font-size: 18px;
  letter-spacing: 0.05em;
  color: #000000;
`;

const FixBox = styled(Box)`
  margin-top: 20px;
`;

const TheTextField = styled(TextField)`
  font-size: 12px;
`;

const Error = styled(Typography).attrs(() => ({ color: 'error' }))`
  margin-top: 12px;
  font-size: 10px;
  text-align: center;
`;

const TheButtonWrapper = styled(ButtonWrapper)<{ isMobile: boolean }>`
  box-sizing: border-box;
  width: 100%;
  margin-top: 28px;
  padding: 10px 24px;
  background: #f9f9f9;

  ${props =>
    props.isMobile &&
    `
    display: flex;
    align-items: center;
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: 200;
    padding: 10px 24px;
    box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.2);
  `}
`;

export default FixAdjustmentDialog;
